import {
  SpaceBetween,
  FormField,
  Input,
  ExpandableSection,
  Checkbox,
  RadioGroup,
  Button,
  Container,
  Header,
  Select,
} from '@amzn/awsui-components-react';
import { useLocalizedText } from '../../../../hooks/localizedText';
import { useState } from 'react';
import { EventInput, WaiverRequirement } from '@amzn/red-velvet-api';
import { OptionalLabel } from './Common';
import { useCustomQuestions, isChoiceType } from '../../../../hooks/events/useCustomQuestions';
import { publishKatalMetric } from '../../../katalAnalytics';
import { FIELD_LIMITS, getCharLimitProps } from '../../../../utils/fieldConstraints';

export interface AdvancedSignupComponentProps {
  eventFields: EventInput;
  updateEventFields: (overrides: Partial<EventInput>) => void;
  isReadOnly?: boolean;
  metricPrefix: string;
  errors?: Record<string, string>;
}

/** The two signup-deadline modes. Stored as a code; the label is resolved at render. */
type DeadlineType = 'none' | 'days';

/**
 * Parses an ISO 8601 duration (e.g. "P7D") into the deadline mode + numeric value.
 *
 * Deliberately returns the mode as a code rather than a `{label, value}` option: the label has to be
 * resolved during render, because t() serves the English fallback until the ARB packs load and a
 * label captured at mount would stay English for the life of the form.
 */
function parseSignupDeadline(deadline: string | undefined): { type: DeadlineType; value: string } {
  const match = deadline?.match(/^P(\d+)D$/);
  return match ? { type: 'days', value: match[1] } : { type: 'none', value: '' };
}

export function AdvancedSignupComponent({
  eventFields,
  updateEventFields,
  isReadOnly,
  metricPrefix,
  errors,
}: AdvancedSignupComponentProps) {
  const namespaces = ['events', 'translation'];
  const { t } = useLocalizedText(namespaces);

  const [waiverUrl, setWaiverUrl] = useState(eventFields?.waiverUrl ?? '');
  const [waiverRequired, setWaiverRequired] = useState(
    eventFields?.waiverRequired === WaiverRequirement.REQUIRED
  );
  const [hasTShirt, setHasTShirt] = useState(eventFields?.hasTShirt ? 'yes' : 'no');
  const [allowGuests, setAllowGuests] = useState(eventFields?.allowGuests ? 'true' : 'false');

  const [parsedDeadline] = useState(() => parseSignupDeadline(eventFields?.signupDeadline));
  const [signupDeadlineTypeCode, setSignupDeadlineTypeCode] = useState<DeadlineType>(
    parsedDeadline.type
  );
  const [signupDeadlineValue, setSignupDeadlineValue] = useState(parsedDeadline.value);
  const [deadlineError, setDeadlineError] = useState('');

  // Built every render so both the list and the selected option pick up the translated labels once
  // the ARB packs resolve. Only the code lives in state.
  const deadlineTypeOptions: Record<DeadlineType, { label: string; value: DeadlineType }> = {
    none: { label: t('shiftStep.noDeadline'), value: 'none' },
    days: { label: t('shiftStep.deadlineDays'), value: 'days' },
  };
  const signupDeadlineType = deadlineTypeOptions[signupDeadlineTypeCode];

  const cq = useCustomQuestions(eventFields?.questions, (questions) => {
    updateEventFields({ questions });
  });

  const syncQuestions = () => {
    updateEventFields({ questions: cq.toApiQuestions() });
  };

  const waiverCharProps = getCharLimitProps(
    waiverUrl,
    FIELD_LIMITS.WAIVER_URL,
    t,
    'shiftStep.waiverUrlConstraint'
  );

  return (
    <ExpandableSection
      headerDescription={t('shiftStep.advancedDescription')}
      headerText={t('shiftStep.advancedHeader')}
      variant="stacked"
    >
      <SpaceBetween size="l">
        <FormField
          description={t('shiftStep.waiverUrlDescription')}
          label={<OptionalLabel>{t('shiftStep.waiverUrl')}</OptionalLabel>}
          constraintText={waiverCharProps.constraintText}
          errorText={errors?.waiverUrl ?? waiverCharProps.errorText}
        >
          <Input
            placeholder="https://example.com/waiver"
            type="url"
            value={waiverUrl}
            onChange={({ detail }) => {
              setWaiverUrl(detail.value);
              if (!detail.value.trim()) {
                setWaiverRequired(false);
              }
            }}
            onBlur={() => {
              const trimmed = waiverUrl.trim();
              updateEventFields({
                waiverUrl: trimmed,
                ...(!trimmed && { waiverRequired: WaiverRequirement.NONE }),
              });
            }}
            readOnly={isReadOnly}
          />
        </FormField>

        {waiverUrl.trim() && (
          <FormField
            description={t('shiftStep.waiverRequiredDescription')}
            label={<OptionalLabel>{t('shiftStep.waiverRequired')}</OptionalLabel>}
            stretch
          >
            <Checkbox
              checked={waiverRequired}
              onChange={({ detail }) => {
                publishKatalMetric(`${metricPrefix}_advancedSignupToggled`, 'waiverRequired');
                setWaiverRequired(detail.checked);
                updateEventFields({
                  waiverRequired: detail.checked
                    ? WaiverRequirement.REQUIRED
                    : WaiverRequirement.NONE,
                });
              }}
              readOnly={isReadOnly}
            >
              {t('shiftStep.waiverIsRequired')}
            </Checkbox>
          </FormField>
        )}

        <FormField
          description={t('shiftStep.tShirtDescription')}
          label={<OptionalLabel>{t('shiftStep.tShirt')}</OptionalLabel>}
          stretch
        >
          <RadioGroup
            items={[
              { label: t('misc.yes'), value: 'yes' },
              { label: t('misc.no'), value: 'no' },
            ]}
            value={hasTShirt}
            onChange={({ detail }) => {
              publishKatalMetric(`${metricPrefix}_advancedSignupToggled`, 'hasTShirt');
              setHasTShirt(detail.value);
              updateEventFields({ hasTShirt: detail.value === 'yes' });
            }}
            readOnly={isReadOnly}
          />
        </FormField>

        <FormField
          description={t('shiftStep.allowGuestsDescription')}
          label={t('shiftStep.allowGuests')}
          stretch
        >
          <RadioGroup
            items={[
              { label: t('misc.yes'), value: 'true' },
              { label: t('misc.no'), value: 'false' },
            ]}
            value={allowGuests}
            onChange={({ detail }) => {
              publishKatalMetric(`${metricPrefix}_advancedSignupToggled`, 'allowGuests');
              setAllowGuests(detail.value);
              updateEventFields({ allowGuests: detail.value === 'true' });
            }}
            readOnly={isReadOnly}
          />
        </FormField>

        <FormField
          description={t('shiftStep.customQuestionsDescription')}
          label={<OptionalLabel>{t('shiftStep.customQuestions')}</OptionalLabel>}
        >
          <SpaceBetween size="l">
            {cq.items.map((item, index) => {
              const qId = item.question.id ?? '';
              return (
                <Container
                  key={qId}
                  header={
                    <Header
                      actions={
                        !isReadOnly && (
                          <Button
                            variant="link"
                            onClick={() => {
                              publishKatalMetric(
                                `${metricPrefix}_advancedSignupToggled`,
                                'questionsRemoved'
                              );
                              cq.removeQuestion(qId);
                            }}
                          >
                            {t('shiftStep.removeQuestion')}
                          </Button>
                        )
                      }
                      variant="h2"
                    >
                      {t('shiftStep.customQuestionHeader', { number: index + 1 })}
                    </Header>
                  }
                >
                  <SpaceBetween size="l">
                    <FormField
                      label={t('shiftStep.questionLabel')}
                      errorText={cq.questionErrors[qId]}
                    >
                      <Input
                        placeholder={t('shiftStep.questionPlaceholder')}
                        value={item.question.title ?? ''}
                        onChange={({ detail }) => {
                          cq.updateTitle(qId, detail.value);
                          if (detail.value.trim()) {
                            cq.clearQuestionError(qId);
                          }
                        }}
                        onBlur={() => {
                          if (!item.question.title?.trim()) {
                            cq.setQuestionError(qId, t('shiftStep.questionTitleRequired'));
                          }
                          syncQuestions();
                        }}
                        readOnly={isReadOnly}
                      />
                    </FormField>

                    <FormField label={t('shiftStep.answerType')}>
                      <Select
                        options={cq.answerTypeOptions}
                        placeholder={t('shiftStep.answerTypePlaceholder')}
                        selectedOption={item.answerTypeOption}
                        onChange={({ detail }) => {
                          cq.updateAnswerType(
                            qId,
                            detail.selectedOption as (typeof cq.answerTypeOptions)[number]
                          );
                        }}
                        readOnly={isReadOnly}
                      />
                    </FormField>

                    {isChoiceType(item.question.answerType) && (
                      <FormField
                        constraintText={t('shiftStep.answerOptionsConstraint')}
                        description={t('shiftStep.answerOptionsDescription')}
                        label={t('shiftStep.answerOptions')}
                        errorText={cq.optionErrors[qId]}
                      >
                        <SpaceBetween size="s">
                          {(item.question.options ?? []).map((option) => {
                            const oId = option.id ?? '';
                            return (
                              <div
                                key={oId}
                                style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}
                              >
                                <div style={{ flex: 1 }}>
                                  <Input
                                    placeholder={t('shiftStep.optionPlaceholder')}
                                    value={option.label ?? ''}
                                    onChange={({ detail }) => {
                                      cq.updateOptionLabel(qId, oId, detail.value);
                                      const allFilled = (item.question.options ?? []).every((o) =>
                                        o.id === option.id ? detail.value.trim() : o.label?.trim()
                                      );
                                      if (allFilled) {
                                        cq.clearOptionError(qId);
                                      }
                                    }}
                                    onBlur={() => {
                                      if (!option.label?.trim()) {
                                        cq.setOptionError(qId, t('shiftStep.optionsNotEmpty'));
                                      }
                                      syncQuestions();
                                    }}
                                    readOnly={isReadOnly}
                                  />
                                </div>
                                {!isReadOnly && (
                                  <Button
                                    variant="normal"
                                    onClick={() => cq.removeOption(qId, oId)}
                                  >
                                    {t('shiftStep.removeOption')}
                                  </Button>
                                )}
                              </div>
                            );
                          })}
                          {!isReadOnly && (item.question.options ?? []).length < cq.maxOptions && (
                            <Button
                              iconName="add-plus"
                              variant="normal"
                              onClick={() => cq.addOption(qId)}
                            >
                              {t('shiftStep.addOption')}
                            </Button>
                          )}
                        </SpaceBetween>
                      </FormField>
                    )}
                  </SpaceBetween>
                </Container>
              );
            })}
            {cq.canAddQuestion && !isReadOnly && (
              <Button
                iconName="add-plus"
                variant="link"
                onClick={() => {
                  publishKatalMetric(`${metricPrefix}_advancedSignupToggled`, 'questions');
                  cq.addQuestion();
                }}
              >
                {t('shiftStep.addCustomQuestion')}
              </Button>
            )}
          </SpaceBetween>
        </FormField>

        <FormField
          description={t('shiftStep.signupDeadlineDescription')}
          label={<OptionalLabel>{t('shiftStep.signupDeadline')}</OptionalLabel>}
          errorText={deadlineError}
        >
          <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
            <div style={{ minWidth: '200px' }}>
              <Select
                selectedOption={signupDeadlineType}
                options={[deadlineTypeOptions.none, deadlineTypeOptions.days]}
                onChange={({ detail }) => {
                  const selected = detail.selectedOption.value as DeadlineType;
                  setSignupDeadlineTypeCode(selected);
                  setSignupDeadlineValue('');
                  setDeadlineError('');
                  if (selected === 'none') {
                    updateEventFields({ signupDeadline: '' });
                  }
                }}
                readOnly={isReadOnly}
              />
            </div>
            {signupDeadlineTypeCode !== 'none' && (
              <>
                <div style={{ width: '80px' }}>
                  <Input
                    type="number"
                    inputMode="numeric"
                    value={signupDeadlineValue}
                    onChange={({ detail }) => {
                      const parsed = parseInt(detail.value);
                      if (detail.value !== '' && (isNaN(parsed) || parsed < 1 || parsed > 30))
                        return;
                      setSignupDeadlineValue(detail.value);
                      if (detail.value.trim()) setDeadlineError('');
                    }}
                    onBlur={() => {
                      if (!signupDeadlineValue.trim()) {
                        setDeadlineError(
                          t('shiftStep.deadlineEmpty', { selection: signupDeadlineType.label })
                        );
                        updateEventFields({ signupDeadline: '' });
                      } else {
                        updateEventFields({
                          signupDeadline: `P${signupDeadlineValue}D`,
                        });
                      }
                    }}
                    readOnly={isReadOnly}
                  />
                </div>
                <span style={{ alignSelf: 'center', whiteSpace: 'nowrap' }}>
                  {t('shiftStep.deadlineDaysLabel')}
                </span>
              </>
            )}
          </div>
        </FormField>
      </SpaceBetween>
    </ExpandableSection>
  );
}
